import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { ModalBody, ModalHeader, TmdxChip, TmdxSwitch } from '@ds';
import { FormRow } from '@features/components/CaseFormBlocks/components';
import { ConditionalRender, InfoBanner } from '@ui';
import { useDuplicateCaseWarning } from '@features/components/CaseFormBlocks/hooks/useDuplicateCaseWarning';
import { CaseGeneral } from '@lp/features/CaseFormSections';
import { getAvailableOrganTypes } from '@lp/features/CaseFormSections/utils';
import { OpoRequestCheckbox, Reallocation, ReallocationCheckbox } from '@lp/features/CaseFormSections/components';
import { useCanCreateKidneyCase } from '@lp/core/hooks/useCanCreateKidneyCase';
import { useMemo } from 'react';
import { Case, CaseOrigin, CaseStatus, OrganType, OutcomeType, ServiceType } from '@/api/apis/case';
import { getActionsForStatus, getLateralityBlockReason, isAllowedStatus, isCaseTrial } from '@/utils/helpers';
import { CaseGeneralFormType } from './config';
import { QuestionaryEditAllowedField } from '../../QuestionaryEditAllowedField/QuestionaryEditAllowedField';

export const EditGeneralCaseForm = ({ caseData, routeProviders }: { caseData: Case; routeProviders: string[] }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<CaseGeneralFormType>();

  const [
    reAllocationCaseTrialProgram,
    originalReAllocationCaseId,
    currentOrganType,
    currentDonorType,
    currentLaterality,
  ] = useWatch<CaseGeneralFormType>({
    control,
    name: ['reAllocationCaseTrialProgram', 'originalReAllocationCaseId', 'organType', 'donorType', 'laterality'],
  });

  const { status, caseId, organType: savedOrganType, questionnaire, trialProgram, outcomeData, serviceType } = caseData;

  const disabled = useMemo(() => {
    const disableMainFields = !isAllowedStatus({
      statusTo: CaseStatus.Declined,
      currentStatus: status,
      prohibitDeclined: true,
    });

    const disableOrganDonorType = !isAllowedStatus({
      statusTo: CaseStatus.Accepted,
      currentStatus: status,
    });

    const disableServiceDetails = status === CaseStatus.Closed || status === CaseStatus.Finished;

    const disableLaterality = status === CaseStatus.Closed;

    return {
      donorId: disableMainFields,
      organType: disableOrganDonorType,
      laterality: disableLaterality,
      donorType: disableOrganDonorType,
      serviceType: disableMainFields,
      opoRequest: disableMainFields,
      serviceDetails: disableServiceDetails,
      reAllocationCase: !isAllowedStatus({
        statusTo: CaseStatus.ClinicalScreening,
        currentStatus: status,
      }),
      nrp: !isAllowedStatus({
        statusTo: CaseStatus.ResourceDeployed,
        currentStatus: status,
        prohibitDeclined: true,
      }),
      additionalServices: disableMainFields,
      serviceRegionId: true,
    };
  }, [status]);

  const disallowLateralityUnset = status === CaseStatus.Finished;

  const canCreateKidneyCase = useCanCreateKidneyCase();
  const availableOrganTypes = useMemo<OrganType[]>(
    () =>
      getAvailableOrganTypes({
        canCreateKidneyCase,
        isEditDraft: true,
        originalOrganType: savedOrganType,
        currentOrganType,
      }),
    [canCreateKidneyCase, savedOrganType, currentOrganType],
  );

  const { isDuplicate } = useDuplicateCaseWarning({ excludeCaseId: caseId, caseStatus: status });

  const isTrial = isCaseTrial(savedOrganType, trialProgram, questionnaire);
  const allowDirectAcquisition = status === CaseStatus.ClinicalScreening && !isTrial;

  const isReallocationDisabled =
    disabled.reAllocationCase || (!!originalReAllocationCaseId && !!reAllocationCaseTrialProgram);

  const isOpoOrigin = caseData.creationSide === CaseOrigin.Opo;
  const isOrganOrDonorTypeChanged = currentOrganType !== caseData.organType || currentDonorType !== caseData.donorType;
  const showOrganChangeAlert = isOrganOrDonorTypeChanged && !!caseData.donorData;

  const caseWasDeclined = !!outcomeData && outcomeData.outcomeType !== OutcomeType.Transplant;
  const { nextStatus } = getActionsForStatus(status, caseWasDeclined);
  const isDirectAcquisitionCase =
    serviceType === ServiceType.DirectAcquisition && status === CaseStatus.ClinicalScreening;
  const effectiveNextStatus = isDirectAcquisitionCase ? CaseStatus.Closed : nextStatus;
  const showLateralityRequiredBanner = getLateralityBlockReason({
    organType: currentOrganType,
    laterality: currentLaterality,
    nextStatus: effectiveNextStatus,
  });

  return (
    <>
      <ModalHeader>
        <h2>Case General Info</h2>
      </ModalHeader>

      <ModalBody>
        {showLateralityRequiredBanner && (
          <div className="mb-4">
            <InfoBanner iconType="warning" title="Laterality required">
              Select Right or Left to continue
            </InfoBanner>
          </div>
        )}

        <div className="flex justify-start gap-5">
          <OpoRequestCheckbox
            hasPermission
            isDisabled={disabled.opoRequest || isOpoOrigin}
            reAllocationCaseId={caseData?.reAllocationCaseId}
          />
          <ReallocationCheckbox isDisabled={isReallocationDisabled} />
        </div>

        {errors.opoRequest && (
          <div className="mt-2">
            <InfoBanner iconType="error">{errors.opoRequest.message}</InfoBanner>
          </div>
        )}

        {errors.txCenterId && (
          <div className="mt-2">
            <InfoBanner iconType="error">{errors.txCenterId.message}</InfoBanner>
          </div>
        )}

        <Reallocation
          disabled={isReallocationDisabled}
          initialReallocationCaseId={caseData.reAllocationCaseId}
          override={false}
        />

        <CaseGeneral
          showRequired={false}
          disabled={disabled}
          hasPermission
          isDuplicate={isDuplicate}
          allowDirectAcquisition={allowDirectAcquisition}
          caseStatus={status}
          disallowLateralityUnset={disallowLateralityUnset}
          availableOrganTypes={availableOrganTypes}
        >
          <FormRow title="Logistics Provider">
            <ConditionalRender show={!!routeProviders.length} placeholder="-">
              <div className="flex flex-wrap gap-2">
                {routeProviders.map((p) => (
                  <TmdxChip size="sm" key={p}>
                    {p}
                  </TmdxChip>
                ))}
              </div>
            </ConditionalRender>
          </FormRow>

          <FormRow title="Additional Services">
            <Controller
              name="additionalServices"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <div className="flex items-center gap-3 text-l-m h-10">
                  <span>No</span>
                  <TmdxSwitch
                    isDisabled={disabled.additionalServices}
                    isSelected={field.value}
                    color="default"
                    size="sm"
                    onValueChange={field.onChange}
                  />
                  <span>Yes</span>
                </div>
              )}
            />
          </FormRow>
        </CaseGeneral>
        {caseData.trialProgram && <QuestionaryEditAllowedField caseData={caseData} />}
        {showOrganChangeAlert && (
          <div className="mt-4">
            <InfoBanner iconType="warning">
              Changing Organ or Donor Type might require updating of <b>OPO Clinical Request form</b>
            </InfoBanner>
          </div>
        )}
      </ModalBody>
    </>
  );
};
